import { useState, forwardRef, useImperativeHandle } from "react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "src/components/ui/table";
import { Button } from "src/components/ui/button";
import { MoreHorizontal, FileText, MailCheck, FileCheck } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "src/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "src/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "src/components/ui/tooltip";
import newDashEmptyGraphic from "../../../assets/images/new-dash-empty.png";
import { useGetInsertionOrdersQuery, useDeleteInsertionOrderMutation, useCloneInsertionOrderMutation } from "../../../redux/billing/billing.endpoints";
import { InsertionOrder } from "../../../redux/billing/billing.types";
import { InsertionOrderModal } from "./create-insertion-order-modal";

interface StatusTooltipIconProps {
  icon: React.ComponentType<any>;
  isActive: boolean;
  tooltipMessage: string;
  className?: string;
}

const StatusTooltipIcon = ({ icon: Icon, isActive, tooltipMessage, className = "" }: StatusTooltipIconProps) => (
  <TooltipProvider delayDuration={0}>
    <Tooltip>
      <TooltipTrigger>
        <Icon
          className={`w-4 h-4 ${className} ${isActive ? 'text-blue-500/75' : 'text-gray-400'}`}
          strokeWidth={2.5}
        />
      </TooltipTrigger>
      <TooltipContent className="px-3 py-1">
        <p className="text-xs">
          {tooltipMessage}
        </p>
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

interface IOTableProps {
  searchValue: string;
}

export interface IOTableRef {
  createNew: () => void;
}

const IOTable = forwardRef<IOTableRef, IOTableProps>(({ searchValue }, ref) => {
  const [isInsertionOrderModalOpen, setIsInsertionOrderModalOpen] = useState(false);
  const [selectedInsertionOrder, setSelectedInsertionOrder] = useState<InsertionOrder | undefined>(undefined);
  const [modalMode, setModalMode] = useState<'create' | 'edit' | 'view'>('create');
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<string | null>(null);

  const { data: insertionOrdersData, isLoading: insertionOrdersLoading, refetch } = useGetInsertionOrdersQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });
  const [deleteInsertionOrder, { isLoading: isDeleting }] = useDeleteInsertionOrderMutation();
  const [cloneInsertionOrder, { isLoading: isCloning }] = useCloneInsertionOrderMutation();

  const handleCreateNewInsertionOrder = () => {
    setSelectedInsertionOrder(undefined);
    setModalMode('create');
    setIsInsertionOrderModalOpen(true);
  };

  useImperativeHandle(ref, () => ({
    createNew: handleCreateNewInsertionOrder,
  }));

  const parseLocalDate = (dateString: string): Date => {
    // If the date string is just YYYY-MM-DD format, parse it as local date
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      const [year, month, day] = dateString.split('-').map(Number);
      return new Date(year, month - 1, day); // month is 0-indexed in Date constructor
    }
    // For full timestamps, use regular Date parsing
    return new Date(dateString);
  };

  const getInsertionOrderStatus = (order: InsertionOrder): 'draft' | 'sent' | 'completed' => {
    if (order.client_signed && order.agency_signed) {
      return 'completed';
    } else if (order.is_active) {
      return 'sent';
    }
    return 'draft';
  };

  const insertionOrders = (insertionOrdersData || [])
    .slice()
    .sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime());

  const handleInsertionOrderClick = (order: InsertionOrder) => {
    setSelectedInsertionOrder(order);
    const status = getInsertionOrderStatus(order);
    if (status === 'draft') {
      setModalMode('edit');
    } else {
      setModalMode('view');
    }
    setIsInsertionOrderModalOpen(true);
  };

  const getStatusColor = (status: string, isDot: boolean = false) => {
    switch (status) {
      case 'draft':
        return isDot ? 'bg-gray-600' : 'text-gray-500';
      case 'sent':
        return isDot ? 'bg-blue-500' : 'text-blue-500';
      case 'completed':
        return isDot ? 'bg-[#31BB64]' : 'text-[#31BB64]';
      default:
        return isDot ? 'bg-gray-600' : 'text-gray-600';
    }
  };

  const renderStatusBreakdown = (order: InsertionOrder) => {
    const status = getInsertionOrderStatus(order);
    if (status !== 'sent') {
      return (
        <span
          className={`inline-flex items-center justify-center rounded-full py-1 px-2 text-xs font-bold bg-white shadow-sm ${getStatusColor(status)} ${status === 'completed' ? 'min-w-[100px]' : ''}`}
        >
          <div className={`size-1 rounded-full mr-1 ${getStatusColor(status, true)}`} />
          {status.charAt(0).toUpperCase() + status.slice(1)}
        </span>
      );
    }

    return (
      <span className="inline-flex items-center rounded-full py-1 px-2 bg-white shadow-sm">
        <div className="flex items-center gap-1">
          {/* Client Section */}
          <div className="flex flex-col items-center gap-0.5 mr-1">
            <div className="flex items-center gap-1">
              <StatusTooltipIcon
                icon={MailCheck}
                isActive={order.client_delivered || order.client_signed}
                tooltipMessage={order.client_delivered ? `Delivered to ${order.client_email}` : `Awaiting delivery to ${order.client_email}`}
              />
              <StatusTooltipIcon
                icon={FileCheck}
                isActive={order.client_signed}
                tooltipMessage={order.client_signed ? `Signed by ${order.client_email}` : `Awaiting signature from ${order.client_email}`}
              />
            </div>
            <span className="text-[8px] text-black/50 font-medium leading-none">
              Client
            </span>
          </div>

          {/* Agent Section */}
          <div className="flex flex-col items-center gap-0.5 mr-1">
            <div className="flex items-center gap-1">
              <StatusTooltipIcon
                icon={MailCheck}
                isActive={order.agency_delivered || order.agency_signed}
                tooltipMessage={order.agency_delivered ? `Delivered to ${order.agent_email}` : `Awaiting delivery to ${order.agent_email}`}
              />
              <StatusTooltipIcon
                icon={FileCheck}
                isActive={order.agency_signed}
                tooltipMessage={order.agency_signed ? `Signed by ${order.agent_email}` : `Awaiting signature from ${order.agent_email}`}
              />
            </div>
            <span className="text-[8px] text-black/50 font-medium leading-none">
              Internal
            </span>
          </div>
        </div>
      </span>
    );
  };

  const formatFlightDates = (startDate: Date, endDate: Date) => {
    const currentYear = new Date().getFullYear();
    const startYear = startDate.getFullYear();
    const endYear = endDate.getFullYear();

    const formatDate = (date: Date, includeYear: boolean = true) => {
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const day = date.getDate().toString().padStart(2, '0');
      if (!includeYear) {
        return `${month}/${day}`;
      }
      const year = date.getFullYear().toString().slice(-2);
      return `${month}/${day}/${year}`;
    };
    return `${formatDate(startDate, startYear !== currentYear)}-${formatDate(endDate, endYear !== currentYear)}`;
  };

  const filteredData = insertionOrders.filter(order => {
    const matchesSearch = order.advertiser_name?.toLowerCase().includes(searchValue.toLowerCase()) ||
      order.campaign_name?.toLowerCase().includes(searchValue.toLowerCase()) ||
      order.agent_name?.toLowerCase().includes(searchValue.toLowerCase());

    return matchesSearch;
  });

  const isEmpty = filteredData.length === 0;
  const hasSearch = searchValue.trim() !== '';

  const handleDeleteInsertionOrder = async(id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await deleteInsertionOrder(id).unwrap();
      setShowDeleteDialog(false);
      setItemToDelete(null);
      refetch();
    } catch (error) {
      console.error('Failed to delete insertion order:', error);
    }
  };

  const handleDeleteClick = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setItemToDelete(id);
    setShowDeleteDialog(true);
  };

  const handleCloneInsertionOrder = async(id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await cloneInsertionOrder(id).unwrap();
    } catch (error) {
      console.error('Failed to clone insertion order:', error);
    }
  };

  if (insertionOrdersLoading) {
    return (
      <div className="flex justify-center items-center py-20">
        <div className="text-gray-500">
          Loading insertion orders...
        </div>
      </div>
    );
  }

  const renderEmptyState = () => {
    return (
      <div className="flex flex-col h-full items-center justify-center bg-white border border-gray-200 rounded-xl py-4 sm:py-32">
        <img
          src={newDashEmptyGraphic}
          className="w-40 h-20"
          alt="Empty state"
        />
        {hasSearch ? (
          <>
            <div className="text-3xl font-light mt-10">
              No insertion orders found.
            </div>
            <div>
              Try changing your search criteria.
            </div>
          </>
        ) : (
          <>
            <div className="flex flex-col items-center mt-10 mb-6">
              <div className="text-3xl font-light">
                No insertion orders yet.
              </div>
              <div>
                Create your first insertion order to get started.
              </div>
            </div>
          </>
        )}
      </div>
    );
  };

  return (
    <>
      <div className="flex-grow min-h-0 flex flex-col">
        {isEmpty ? (
          renderEmptyState()
        ) : (
          <div className="w-full bg-white rounded-xl border border-gray-200 p-4 pt-2 flex-grow min-h-0 flex flex-col">
            <div className="flex-grow min-h-0 overflow-auto">
              <Table>
                <TableHeader className="sticky top-0 bg-white z-10">
                  <TableRow className="border-none">
                    <TableHead className="w-[78px] max-w-[78px] py-1.5" />
                    <TableHead className="text-gray-600 py-1.5">
                      Last Updated
                    </TableHead>
                    <TableHead className="text-gray-600 py-1.5">
                      Advertiser
                    </TableHead>
                    <TableHead className="text-gray-600 py-1.5">
                      Campaign
                    </TableHead>
                    <TableHead className="text-gray-600 py-1.5">
                      Flight Dates
                    </TableHead>
                    <TableHead className="text-gray-600 py-1.5">
                      Owner
                    </TableHead>
                    <TableHead className="text-gray-600 py-1.5 text-center">
                      Status
                    </TableHead>
                    <TableHead className="w-[30px] py-1.5" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredData.map((order, index) => (
                    <TableRow
                      key={order.id}
                      className={`hover:bg-gray-100 transition-colors border-b border-gray-100 last:border-b-0 ${
                        index % 2 === 0 ? 'bg-[#F8F9FA]' : 'bg-[#F2F3F5]'
                      } cursor-pointer`}
                      onClick={() => handleInsertionOrderClick(order)}
                    >
                      <TableCell className={`text-center py-1.5 ${index === 0 ? 'rounded-tl-xl' : index === filteredData.length - 1 ? 'rounded-bl-xl' : ''}`}>
                        <span className="max-w-[78px] inline-flex items-center justify-center rounded-full py-1 px-2 text-xs font-bold bg-white shadow-sm text-gray-700">
                          <div className="flex items-center gap-1">
                            <FileText className="w-3 h-3" />
                            IO
                          </div>
                        </span>
                      </TableCell>
                      <TableCell className="py-1.5">
                        {new Date(order.updated_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </TableCell>
                      <TableCell className="py-1.5">
                        {order.advertiser_name || 'N/A'}
                      </TableCell>
                      <TableCell className="py-1.5">
                        {order.campaign_name || 'N/A'}
                      </TableCell>
                      <TableCell className="py-1.5">
                        {formatFlightDates(parseLocalDate(order.start_date), parseLocalDate(order.end_date))}
                      </TableCell>
                      <TableCell className="py-1.5">
                        {order.agent_name}
                      </TableCell>
                      <TableCell className="py-1.5 text-center">
                        {renderStatusBreakdown(order)}
                      </TableCell>
                      <TableCell
                        className={`${index === 0 ? 'rounded-tr-xl' : index === filteredData.length - 1 ? 'rounded-br-xl' : ''} py-1.5`}
                        onClick={e => e.stopPropagation()}
                      >
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              className="h-8 w-8 p-0"
                              disabled={isDeleting || isCloning}
                            >
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              className="cursor-pointer"
                              onClick={(e) => handleCloneInsertionOrder(order.id, e)}
                              disabled={isCloning}
                            >
                              {isCloning ? 'Cloning...' : 'Clone'}
                            </DropdownMenuItem>
                            <Dialog
                              open={showDeleteDialog}
                              onOpenChange={setShowDeleteDialog}
                            >
                              <DialogTrigger asChild>
                                <DropdownMenuItem
                                  className="cursor-pointer"
                                  onClick={(e) => handleDeleteClick(order.id, e)}
                                  disabled={isDeleting}
                                  onSelect={(e) => e.preventDefault()}
                                >
                                  Delete
                                </DropdownMenuItem>
                              </DialogTrigger>
                              <DialogContent
                                onClick={e => e.stopPropagation()}
                                className="bg-white"
                              >
                                <DialogHeader>
                                  <DialogTitle>
                                    Delete Insertion Order
                                  </DialogTitle>
                                  <DialogDescription>
                                    Are you sure you want to delete this insertion order? This action cannot be undone.
                                  </DialogDescription>
                                </DialogHeader>
                                <DialogFooter>
                                  <Button
                                    variant="outline"
                                    onClick={() => setShowDeleteDialog(false)}
                                  >
                                    Cancel
                                  </Button>
                                  <Button
                                    className="bg-red-600 text-white hover:bg-red-700"
                                    onClick={(e) => itemToDelete && handleDeleteInsertionOrder(itemToDelete, e)}
                                    disabled={isDeleting}
                                  >
                                    {isDeleting ? 'Deleting...' : 'Delete'}
                                  </Button>
                                </DialogFooter>
                              </DialogContent>
                            </Dialog>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}
      </div>

      {/* Insertion Order Modal */}
      <InsertionOrderModal
        open={isInsertionOrderModalOpen}
        onOpenChange={setIsInsertionOrderModalOpen}
        initialData={selectedInsertionOrder}
        mode={modalMode}
        onSuccess={() => refetch()}
      />
    </>
  );
});

IOTable.displayName = 'IOTable';

export default IOTable;